import React, { useEffect, useState, useMemo, useCallback } from 'react';
import api, { API } from '../api/client';
import { Plus, X, ShieldCheck, Loader2, Trash2, Calendar, CheckCircle2, Circle, FileDown, Camera, Upload, ChevronDown, ChevronUp } from 'lucide-react';
import CsvImport from '../components/app/CsvImport';
import SearchBar, { matches } from '../components/app/SearchBar';
import SitePicker from '../components/app/SitePicker';
import { useSyncListener } from '../hooks/useDataSync';

const fileUrl = (path) => {
  const token = localStorage.getItem('sf_token');
  return `${API}/maintenance/files/${path}?auth=${token}`;
};

const TYPE_LABELS = {
  limpieza: 'Limpieza',
  revision_impermeabilizacion: 'Revisión impermeabilización',
  revision_grietas: 'Revisión grietas',
  desbroce: 'Desbroce vegetación',
  revision_accesos: 'Revisión accesos',
  revision_vallado: 'Revisión vallado',
  inspeccion_general: 'Inspección general',
  otro: 'Otro',
};

// Checklists por entorno (urbano vs rural)
const CHECKLISTS = {
  urbano: [
    'Estado general de la cubierta',
    'Sellado y juntas de impermeabilización',
    'Detectar humedades o filtraciones',
    'Ausencia de grietas o desconchones',
    'Limpieza estética visible',
    'Cableado en buen estado',
  ],
  rural: [
    'Nivel de vegetación en perímetro',
    'Estado del acceso rodado / camino',
    'Vallado perimetral íntegro',
    'Ausencia de vertidos o basura',
    'Puerta de acceso y candado',
    'Cimentación torre / apoyo',
  ],
};

const STATUS_LABEL = {
  planificada: '📅 Planificada',
  en_curso: '🔧 En curso',
  completada: '✅ Completada',
  cancelada: '⛔ Cancelada',
};

const Preventivo = () => {
  const [items, setItems] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expanded, setExpanded] = useState({}); // task_id -> boolean
  const [uploading, setUploading] = useState(null); // { task_id, kind }
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([api.get('/preventive'), api.get('/sites/lite')]);
      setItems(p.data); setSites(s.data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useSyncListener(load, ['preventive', 'sites']);

  const siteMap = useMemo(() => Object.fromEntries(sites.map((s) => [s.id, s])), [sites]);

  const filtered = useMemo(() => items.filter((t) => matches(search,
    t.title, t.description, t.notes, TYPE_LABELS[t.type],
    siteMap[t.site_id]?.name, siteMap[t.site_id]?.municipality, siteMap[t.site_id]?.address,
  )), [items, search, siteMap]);

  const del = async (id) => {
    if (!confirm('¿Eliminar esta tarea preventiva?')) return;
    await api.delete(`/preventive/${id}`); await load();
  };

  const uploadPhoto = async (task_id, kind, file) => {
    if (!file) return;
    setUploading({ task_id, kind });
    const fd = new FormData();
    fd.append('file', file);
    fd.append('kind', kind);
    try {
      await api.post(`/preventive/${task_id}/photos`, fd);
      await load();
    } catch (e) {
      alert('Error: ' + (e?.response?.data?.detail || e.message));
    } finally { setUploading(null); }
  };

  const delPhoto = async (task_id, photo_id) => {
    if (!confirm('¿Eliminar foto?')) return;
    await api.delete(`/preventive/${task_id}/photos/${photo_id}`);
    await load();
  };

  const toggleCheck = async (task, index) => {
    const cl = [...(task.checklist || [])];
    cl[index] = { ...cl[index], done: !cl[index].done };
    await api.put(`/preventive/${task.id}`, {
      site_id: task.site_id, type: task.type, title: task.title, description: task.description,
      scheduled_date: task.scheduled_date, frequency_months: task.frequency_months, site_env: task.site_env,
      estimated_cost: task.estimated_cost, status: task.status, assigned_to: task.assigned_to, checklist: cl, notes: task.notes,
    });
    // Optimistic: reload full to keep photos array in sync
    await load();
  };

  const complete = async (task) => {
    await api.put(`/preventive/${task.id}`, {
      site_id: task.site_id, type: task.type, title: task.title, description: task.description,
      scheduled_date: task.scheduled_date, frequency_months: task.frequency_months, site_env: task.site_env,
      estimated_cost: task.estimated_cost, status: 'completada', assigned_to: task.assigned_to, checklist: task.checklist, notes: task.notes,
    });
    await load();
  };

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto" data-testid="preventive-page">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-6">
        <div>
          <div className="text-xs font-bold tracking-widest text-[#A3FF12]">MANTENIMIENTO · PREVENTIVO</div>
          <h1 className="text-3xl sm:text-4xl font-black mt-1">Plan preventivo</h1>
          <p className="text-white/50 text-sm mt-1">Protocolos diferenciados (urbano/rural) · ~400 €/site/año.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <CsvImport entity="preventive" onImported={load}/>
          <button onClick={() => { setEditing(null); setShowModal(true); }} data-testid="btn-new-preventive"
            className="flex items-center gap-2 bg-[#A3FF12] text-black font-black px-4 py-2 rounded-xl hover:bg-[#8ee00a] text-sm">
            <Plus size={16}/> Nueva tarea
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24"><Loader2 className="animate-spin text-[#A3FF12]" size={32}/></div>
      ) : items.length === 0 ? (
        <div className="text-center py-24 border border-dashed border-white/10 rounded-2xl">
          <ShieldCheck className="mx-auto text-white/20 mb-2" size={40}/>
          <p className="text-white/50">Sin tareas preventivas programadas.</p>
        </div>
      ) : (
        <div className="space-y-3">
          <SearchBar value={search} onChange={setSearch} placeholder="Buscar por título, emplazamiento, tipo…" testId="preventive-search"/>
          {filtered.length === 0 && (
            <div className="text-center py-10 text-white/40 text-sm">Ninguna tarea coincide con «{search}».</div>
          )}
          {filtered.map((t) => {
            const done = (t.checklist || []).filter((c) => c.done).length;
            const total = (t.checklist || []).length;
            return (
              <div key={t.id} className="bg-black/30 border border-white/10 hover:border-[#A3FF12]/30 rounded-2xl p-4" data-testid={`preventive-${t.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-[10px] uppercase font-bold text-white/50 bg-white/5 px-2 py-0.5 rounded-full">{TYPE_LABELS[t.type]}</span>
                      <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${t.site_env === 'rural' ? 'text-green-300 bg-green-500/10' : 'text-blue-300 bg-blue-500/10'}`}>{t.site_env}</span>
                      <span className="text-[10px] text-white/50">{STATUS_LABEL[t.status]}</span>
                    </div>
                    <div className="text-white font-bold">{t.title}</div>
                    <div className="text-white/50 text-xs mt-0.5 flex items-center gap-3 flex-wrap">
                      <span>📍 {siteMap[t.site_id]?.name || 'Sin site'}</span>
                      <span className="flex items-center gap-1"><Calendar size={11}/> {t.scheduled_date}</span>
                      <span>{t.estimated_cost} €</span>
                      <span>cada {t.frequency_months}m</span>
                      {t.photos?.length > 0 && (
                        <span className="flex items-center gap-1 text-[#A3FF12]/80"><Camera size={11}/> {t.photos.length} foto{t.photos.length !== 1 ? 's' : ''}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {t.status !== 'completada' && (
                      <button onClick={() => complete(t)} className="text-xs bg-[#A3FF12] text-black font-bold px-3 py-1.5 rounded-lg">✓ Completar</button>
                    )}
                    {t.status === 'completada' && (
                      <a href={`${api.defaults.baseURL}/preventive/${t.id}/report.pdf?auth=${encodeURIComponent(localStorage.getItem('sf_token') || '')}`}
                         target="_blank" rel="noopener noreferrer"
                         data-testid={`btn-download-report-${t.id}`}
                         className="text-xs border border-[#A3FF12]/40 text-[#A3FF12] hover:bg-[#A3FF12]/10 font-bold px-3 py-1.5 rounded-lg flex items-center gap-1">
                        <FileDown size={12}/> Informe PDF
                      </a>
                    )}
                    <button onClick={() => del(t.id)} className="text-red-400 hover:text-red-300 p-1.5"><Trash2 size={14}/></button>
                  </div>
                </div>
                {total > 0 && (
                  <div className="mt-3 pt-3 border-t border-white/10">
                    <div className="text-xs text-white/50 mb-2">Checklist · {done}/{total}</div>
                    <div className="grid sm:grid-cols-2 gap-1">
                      {t.checklist.map((c, i) => (
                        <button key={i} onClick={() => toggleCheck(t, i)} className="flex items-center gap-2 text-left text-xs text-white/70 hover:text-white py-1">
                          {c.done ? <CheckCircle2 size={14} className="text-[#A3FF12] flex-shrink-0"/> : <Circle size={14} className="text-white/30 flex-shrink-0"/>}
                          <span className={c.done ? 'line-through text-white/40' : ''}>{c.item}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Photos antes / despues */}
                <div className="mt-3 pt-3 border-t border-white/10">
                  <button onClick={() => setExpanded((s) => ({ ...s, [t.id]: !s[t.id] }))}
                    data-testid={`btn-toggle-photos-${t.id}`}
                    className="w-full flex items-center justify-between text-xs text-white/60 hover:text-white">
                    <span className="flex items-center gap-2"><Camera size={14}/> Fotos antes/después</span>
                    {expanded[t.id] ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
                  </button>
                  {expanded[t.id] && (
                    <div className="mt-3 grid sm:grid-cols-2 gap-4" data-testid={`photos-panel-${t.id}`}>
                      {['antes', 'despues'].map((kind) => {
                        const list = (t.photos || []).filter((p) => p.kind === kind);
                        const isUploading = uploading?.task_id === t.id && uploading?.kind === kind;
                        return (
                          <div key={kind} className="bg-black/30 border border-white/5 rounded-xl p-3">
                            <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2 flex items-center justify-between">
                              <span>{kind === 'antes' ? 'Antes' : 'Después'}</span>
                              <span className="text-white/30">{list.length}</span>
                            </div>
                            <label className="border border-dashed border-white/15 hover:border-[#A3FF12]/50 rounded-lg py-3 flex flex-col items-center justify-center cursor-pointer text-white/50 hover:text-[#A3FF12] transition-colors">
                              {isUploading ? <Loader2 className="animate-spin" size={18}/> : <Upload size={16}/>}
                              <span className="text-[10px] mt-1">Subir foto {kind}</span>
                              <input type="file" accept="image/*" className="hidden"
                                data-testid={`upload-preventive-photo-${kind}-${t.id}`}
                                onChange={(e) => uploadPhoto(t.id, kind, e.target.files[0])}/>
                            </label>
                            {list.length > 0 && (
                              <div className="grid grid-cols-3 gap-1.5 mt-2">
                                {list.map((p) => (
                                  <div key={p.id} className="relative group rounded-lg overflow-hidden border border-white/10 aspect-square">
                                    <img src={fileUrl(p.storage_path)} alt={p.original_filename} className="w-full h-full object-cover"/>
                                    <button onClick={() => delPhoto(t.id, p.id)}
                                      data-testid={`btn-del-photo-${p.id}`}
                                      className="absolute top-1 right-1 bg-black/70 text-red-400 rounded p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                      <Trash2 size={11}/>
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && <PreventiveModal sites={sites} editing={editing} onClose={() => setShowModal(false)} onSaved={async () => { setShowModal(false); await load(); }}/>}
    </div>
  );
};

const PreventiveModal = ({ sites, editing, onClose, onSaved }) => {
  const [form, setForm] = useState(editing || {
    site_id: '',
    site_code_manual: '',
    type: 'inspeccion_general',
    title: 'Inspección general',
    description: '',
    scheduled_date: new Date().toISOString().slice(0, 10),
    frequency_months: 12,
    site_env: 'urbano',
    estimated_cost: 400,
    status: 'planificada',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.site_id && !form.site_code_manual) {
      alert('Selecciona un emplazamiento o introduce un código manual');
      return;
    }
    setSaving(true);
    try {
      const checklist = (CHECKLISTS[form.site_env] || []).map((item) => ({ item, done: false }));
      const payload = { ...form, checklist };
      if (payload.site_code_manual && !payload.site_id) payload.site_id = payload.site_code_manual;
      if (editing?.id) await api.put(`/preventive/${editing.id}`, payload);
      else await api.post('/preventive', payload);
      onSaved();
    } catch (e) { alert('Error: ' + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} data-testid="preventive-modal"
        className="bg-[#0a0a0a] border border-white/10 rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="p-5 border-b border-white/10 flex justify-between">
          <h3 className="text-lg font-black">Nueva tarea preventiva</h3>
          <button type="button" onClick={onClose} className="text-white/40 hover:text-white"><X size={22}/></button>
        </div>
        <div className="p-5 space-y-3">
          <SitePicker sites={sites} siteId={form.site_id} manualCode={form.site_code_manual}
            onChange={({ siteId, manualCode }) => setForm({ ...form, site_id: siteId, site_code_manual: manualCode })}
            testIdPrefix="prev"/>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs uppercase text-white/60">Tipo</span>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 mt-1">
                {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs uppercase text-white/60">Entorno</span>
              <select value={form.site_env} onChange={(e) => setForm({ ...form, site_env: e.target.value })} className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 mt-1">
                <option value="urbano">Urbano</option>
                <option value="rural">Rural</option>
              </select>
            </label>
          </div>
          <label className="block">
            <span className="text-xs uppercase text-white/60">Título</span>
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 mt-1"/>
          </label>
          <div className="grid grid-cols-3 gap-3">
            <label className="block">
              <span className="text-xs uppercase text-white/60">Fecha</span>
              <input type="date" required value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 mt-1"/>
            </label>
            <label className="block">
              <span className="text-xs uppercase text-white/60">Frec. (meses)</span>
              <select value={form.frequency_months} onChange={(e) => setForm({ ...form, frequency_months: parseInt(e.target.value) })} className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 mt-1">
                <option value={3}>3</option>
                <option value={6}>6</option>
                <option value={12}>12</option>
                <option value={24}>24</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs uppercase text-white/60">Coste €</span>
              <input type="number" value={form.estimated_cost} onChange={(e) => setForm({ ...form, estimated_cost: parseFloat(e.target.value) || 0 })} className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 mt-1"/>
            </label>
          </div>
          <label className="block">
            <span className="text-xs uppercase text-white/60">Descripción</span>
            <textarea rows="2" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 mt-1"/>
          </label>
          <div className="text-xs text-white/40 bg-black/30 border border-white/5 rounded-lg p-2">
            Se generará automáticamente un checklist estándar ({(CHECKLISTS[form.site_env] || []).length} items) para entorno {form.site_env}.
          </div>
        </div>
        <div className="p-5 border-t border-white/10 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 border border-white/10 rounded-xl text-sm">Cancelar</button>
          <button type="submit" disabled={saving} data-testid="btn-save-preventive"
            className="bg-[#A3FF12] text-black font-black px-4 py-2 rounded-xl disabled:opacity-50">
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Preventivo;
